import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Lock, Eye, EyeOff, Check, X, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { setPassword as setPwdApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AuthShell } from "./AuthShell";

export default function SetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const { loginAs } = useAuth();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Setup link is invalid or expired. Request a new code.");
      navigate("/auth");
    }
  }, [token, navigate]);

  // Live rules
  const minLen = pwd.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const matches = pwd && pwd === confirm;
  const allOk = minLen && hasLetter && hasDigit && matches;

  const submit = async (e) => {
    e?.preventDefault();
    if (!allOk) {
      toast.error("Please satisfy all password rules");
      return;
    }
    setLoading(true);
    try {
      const res = await setPwdApi({ setup_token: token, password: pwd });
      loginAs(res.data.access_token, res.data.user);
      toast.success("Password set — you're in");
      navigate("/chat");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="mb-8 flex items-center justify-between">
        <Logo />
        <ThemeToggle />
      </div>

      <div className="mb-8">
        <div className="mb-2 text-xs uppercase tracking-[0.25em] text-primary">Final step</div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Set a password.</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          For <span className="font-medium text-foreground">{email}</span>. Use this every time you sign in — no more OTPs.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
        {/* New password */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex w-12 items-center justify-center text-muted-foreground">
            <Lock className="h-4 w-4" />
          </div>
          <Input
            type={showPwd ? "text" : "password"}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="New password"
            autoComplete="new-password"
            autoFocus
            className="h-12 rounded-xl bg-background/60 pl-12 pr-12"
            data-testid="set-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label={showPwd ? "Hide password" : "Show password"}
            data-testid="set-password-toggle-visibility"
          >
            {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>

        {/* Confirm */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex w-12 items-center justify-center text-muted-foreground">
            <Lock className="h-4 w-4" />
          </div>
          <Input
            type={showPwd ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm password"
            autoComplete="new-password"
            className="h-12 rounded-xl bg-background/60 pl-12 pr-4"
            data-testid="set-password-confirm-input"
          />
        </div>

        {/* Rules */}
        <ul className="space-y-1 rounded-xl border border-border/50 bg-background/40 p-3 text-xs" data-testid="set-password-rules">
          <Rule ok={minLen} label="At least 8 characters" />
          <Rule ok={hasLetter} label="Contains a letter" />
          <Rule ok={hasDigit} label="Contains a number" />
          <Rule ok={matches} label="Passwords match" />
        </ul>

        <Button
          type="submit"
          disabled={loading || !allOk}
          className="h-12 w-full rounded-xl text-[15px] font-medium"
          data-testid="set-password-submit-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Set password & continue <ArrowRight className="ml-2 h-4 w-4" /></>}
        </Button>

        <div className="flex items-center justify-center gap-1.5 pt-2 text-[11px] text-muted-foreground">
          <Shield className="h-3 w-3" /> Stored hashed (bcrypt) · never logged or sold
        </div>
      </form>
    </AuthShell>
  );
}

function Rule({ ok, label }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? "text-emerald-500" : "text-muted-foreground"}`}>
      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
      <span>{label}</span>
    </li>
  );
}
